/**
 * CreatorStatsGrid
 * 4x animated stat cards for the creator earnings dashboard
 */

import { AnimatedNumber } from '@components/ui/AnimatedNumber';
import { GlowStatCard } from '@components/ui/GlowStatCard';
import type { EarningsData, FollowerPreview } from '@hooks/useCreatorEarnings';
import type { CreatorStatus } from '@parlay-picker/shared/creatorPayout';
import { formatCents } from '@parlay-picker/shared/formatting';
import type { ReactElement } from 'react';

interface CreatorStatsGridProps {
  status: CreatorStatus;
  earnings: EarningsData | null;
  followerPreview: FollowerPreview | null;
}

export function CreatorStatsGrid({
  status,
  earnings,
  followerPreview,
}: CreatorStatsGridProps): ReactElement {
  const latestPayout = earnings?.payouts?.[0];

  return (
    <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3 mb-6">
      <GlowStatCard label="Total Earned" sub="All time" accent="success">
        <span className="text-theme-success">{formatCents(status.totalEarnedCents)}</span>
      </GlowStatCard>

      <GlowStatCard
        label="Latest Payout"
        sub={latestPayout ? latestPayout.periodLabel : 'No payouts yet'}
        accent="default"
      >
        {latestPayout ? formatCents(latestPayout.creatorShareCents) : '-'}
      </GlowStatCard>

      <GlowStatCard
        label="Eligible Followers"
        sub={
          followerPreview
            ? `of ${followerPreview.totalCount} total`
            : `${status.followerCount} total`
        }
        accent="default"
      >
        <AnimatedNumber value={followerPreview?.eligibleCount ?? 0} />
      </GlowStatCard>

      <GlowStatCard label="Stripe Connect" accent={status.hasStripeConnect ? 'success' : 'warning'}>
        <span
          className={
            status.hasStripeConnect ? 'text-theme-success text-lg' : 'text-theme-warning text-lg'
          }
        >
          {status.hasStripeConnect ? 'Connected' : 'Not Setup'}
        </span>
      </GlowStatCard>
    </div>
  );
}
